import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { themes, scrollTop } from "services/constants";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import {
  CustomButton,
  InfoMessage,
} from "components/CustomComponents/CustomButton";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { shiftHeader } from "pages/Administration/AdministrationHeaders";
import { tableRowSize } from "services/constants";
import { CreateShiftPanel } from "./CreateShiftPanel";
import { ViewShiftPanel } from "./ViewShfitPanel";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";

const keys = [
  "name",
  "duration",
  "start_time",
  "end_time",
  "is_break_allowed",
  "break_duration",
];

export const Shifts = () => {
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [shiftDetails, setShiftDetails] = useState([]);
  const [editShiftDetails, setEditShiftDetails] = useState({});
  const [updateData, setUpdateData] = useState(true);
  const [getId, setGetId] = useState("");
  const containerRef = useRef(null);
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Shift",
    "Shift"
  );

  const {
    result: shiftData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}administration/shiftroster/shift/shifts`);

  let sortedShiftData = shiftData.sort((a, b) => {
    const durationA = a.duration || "00:00";
    const durationB = b.duration || "00:00";
    const startTimeA = a.start_time || "00:00";
    const startTimeB = b.start_time || "00:00";
    if (durationA !== durationB) {
      return durationA.localeCompare(durationB);
    }
    return startTimeA.localeCompare(startTimeB);
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (sortedShiftData?.length !== 0 && sortedShiftData) {
      const item = sortedShiftData.find((d) => d.id === getId);
      setEditShiftDetails(item);
    }
  }, [getId, sortedShiftData]);

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);

  const handleCreateShift = () => {
    setIsCreatePanelOpen(true);
  };

  const handleViewShift = () => {
    setIsViewPanelOpen(true);
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewClosePanel = () => {
    setIsViewPanelOpen(false);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setShiftDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  return updateData && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Shift
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define shift timings and break for employee shifts
          </Typography>
        </Box>
        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Shift Records"
            filterHeaders={shiftHeader}
            data={sortedShiftData}
            filteredData={shiftDetails}
            searchKeys={keys}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={
              <CustomButton
                actionFuntion={() => {
                  if (check_access) {
                    handleCreateShift();
                  } else {
                    showToast(
                      "error",
                      `Access denied: You are not authorized to create shift`
                    );
                  }
                }}
                actionName="New"
                typeName="button"
              />
            }
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSize}>
                    {shiftHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {shiftDetails?.length === 0 ? (
                    <InfoMessage dataLength={sortedShiftData} />
                  ) : (
                    shiftDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value) => (
                        <Mui.TableRow sx={tableRowSize}>
                          {shiftHeader.map((head) => (
                            <Mui.TableCell
                              onClick={() => {
                                handleViewShift();
                                setEditShiftDetails(value);
                              }}
                            >
                              {head.key === "is_break_allowed"
                                ? value[head.key]
                                  ? "Yes"
                                  : "No"
                                : value[head.key] || "N/A"}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {shiftDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={shiftDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          isCreatePanelOpen ? handleCreateClosePanel() : handleViewClosePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.themes?.whiteColor,
          },
        }}
      >
        {isCreatePanelOpen ? (
          <CreateShiftPanel
            onClose={handleCreateClosePanel}
            reCallApi={reCallApi}
            setUpdateData={setUpdateData}
          />
        ) : (
          <ViewShiftPanel
            onClose={handleViewClosePanel}
            editShiftDetails={editShiftDetails}
            reCallApi={reCallApi}
            setUpdateData={setUpdateData}
            setGetId={setGetId}
            screenAccess={check_access}
          />
        )}
      </Drawer>
    </React.Fragment>
  );
};
